/**
 * Centralized routing configuration
 * All route paths and navigation logic should reference these constants
 */

/**
 * Route patterns for React Router
 * Use these for defining routes and access control
 */
export const ROUTES = {
  // Public routes
  SIGN_UP: '/sign-up',
  LOGIN: '/login',
  LOGOUT: '/logout',
  AUTH_CALLBACK: '/auth/callback',
  WELCOME: '/welcome',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',

  // User routes
  HOME: '/',
  AUCTIONS: '/auctions',
  AUCTION_HOUSE: '/:slug',
  CATALOG: '/:slug/:catalogId',
  ITEM: '/:slug/:catalogId/:lotNumber',
  PAGES: '/pages/:pageId',
  SETTINGS: '/settings',

  // Admin routes
  ADMIN: '/admin',
  ADMIN_ERROR_DEMO: '/admin/error-demo',
  ADMIN_SETTINGS: '/admin/settings',
  ADMIN_ASSIGN_ADMINS: '/admin/assign-admins',
  ADMIN_SALE_IMPORTS: '/admin/sale-imports',
  ADMIN_AUCTION_HOUSES: '/admin/auction-houses',
  ADMIN_EMAIL_TEMPLATES: '/admin/email-templates',
  ADMIN_EMAIL_LOGS: '/admin/email-logs',
  ADMIN_CATALOGS: '/admin/:slug',
  ADMIN_NEW_CATALOG: '/admin/:slug/new',
  ADMIN_CATALOG_DETAIL: '/admin/:slug/:catalogId',
  ADMIN_CATALOG_ITEM: '/admin/:slug/:catalogId/:lotNumber',
  ADMIN_ITEMS: '/admin/:slug/items',
  ADMIN_ITEM_DETAIL: '/admin/:slug/items/:itemId',
  ADMIN_INSIGHTS: '/admin/:slug/insights',
  ADMIN_INSIGHTS_UPGRADE: '/admin/:slug/insights/upgrade',
  ADMIN_INSIGHTS_UNDERBIDDERS: '/admin/:slug/insights/underbidders',
  ADMIN_INSIGHTS_TOP_REVENUE: '/admin/:slug/insights/top-revenue',
  ADMIN_CUSTOMERS: '/admin/:slug/customers',
  ADMIN_BIDDERS: '/admin/:slug/bidders',

  // Live auction routes
  LIVE_AUCTION: '/live/:slug/:catalogId',
} as const;

/**
 * Route builders - generate paths from patterns
 * Single source of truth: changes to ROUTES automatically update these
 */
export const build = {
  adminHome: () => ROUTES.ADMIN,
  adminAssignAdmins: () => ROUTES.ADMIN_ASSIGN_ADMINS,
  adminSaleImports: () => ROUTES.ADMIN_SALE_IMPORTS,
  adminAuctionHouses: () => ROUTES.ADMIN_AUCTION_HOUSES,
  adminEmailTemplates: () => ROUTES.ADMIN_EMAIL_TEMPLATES,
  adminEmailLogs: () => ROUTES.ADMIN_EMAIL_LOGS,
  auctionHouse: (slug: string) => ROUTES.AUCTION_HOUSE.replace(':slug', slug),
  catalog: (slug: string, catalogId: string) =>
      ROUTES.CATALOG.replace(':slug', slug).replace(':catalogId', catalogId),
    item: (slug: string, catalogId: string, lotNumber: string) =>
      ROUTES.ITEM
          .replace(':slug', slug)
          .replace(':catalogId', catalogId)
          .replace(':lotNumber', lotNumber),
  pages: (pageId: string) => ROUTES.PAGES.replace(':pageId', pageId),
  adminCatalogs: (slug: string) => ROUTES.ADMIN_CATALOGS.replace(':slug', slug),
  adminNewCatalog: (slug: string) => ROUTES.ADMIN_NEW_CATALOG.replace(':slug', slug),
  adminCatalogDetail: (slug: string, catalogId: string) =>
    ROUTES.ADMIN_CATALOG_DETAIL.replace(':slug', slug).replace(':catalogId', catalogId),
    adminCatalogItem: (slug: string, catalogId: string, lotNumber: string) =>
    ROUTES.ADMIN_CATALOG_ITEM
      .replace(':slug', slug)
      .replace(':catalogId', catalogId)
        .replace(':lotNumber', lotNumber),
  adminItems: (slug: string) =>
    ROUTES.ADMIN_ITEMS.replace(':slug', slug),
  adminItemDetail: (slug: string, itemId: string) =>
    ROUTES.ADMIN_ITEM_DETAIL
      .replace(':slug', slug)
      .replace(':itemId', itemId),
  adminInsights: (slug: string) =>
    ROUTES.ADMIN_INSIGHTS.replace(':slug', slug),
  adminInsightsUpgrade: (slug: string) =>
      ROUTES.ADMIN_INSIGHTS_UPGRADE.replace(':slug', slug),
  adminInsightsUnderbidders: (slug: string) =>
    ROUTES.ADMIN_INSIGHTS_UNDERBIDDERS.replace(':slug', slug),
  adminInsightsTopRevenue: (slug: string) =>
    ROUTES.ADMIN_INSIGHTS_TOP_REVENUE.replace(':slug', slug),
  adminCustomers: (slug: string) =>
    ROUTES.ADMIN_CUSTOMERS.replace(':slug', slug),
  adminBidders: (slug: string) =>
      ROUTES.ADMIN_BIDDERS.replace(':slug', slug),
  liveAuction: (slug: string, catalogId: string) =>
    ROUTES.LIVE_AUCTION
      .replace(':slug', slug)
      .replace(':catalogId', catalogId),
} as const;

/**
 * Insights routes mapping - maps insight keys to their route builder functions
 * Import InsightKey type to ensure type safety
 */
import type { InsightKey } from '@/components/domain/insights';

export const INSIGHTS_ROUTES: Partial<Record<InsightKey, (slug: string) => string>> = {
  smartUnderbidderOutreach: build.adminInsightsUnderbidders,
  topRevenueClients: build.adminInsightsTopRevenue,
};

/**
 * Extract route keys as a type
 * This ensures access.ts must define access for every route
 */
export type RouteKey = typeof ROUTES[keyof typeof ROUTES];

/**
 * Local storage key for storing the return URL after login
 */
export const RETURN_TO_KEY = 'monabid_return_to';

/**
 * Helper to save current location before navigating to login
 * Validates that the path is internal to prevent open redirect attacks
 */
export const saveReturnTo = (path: string) => {
  // Don't save login/callback/welcome pages as return destinations
  if (
    path === ROUTES.LOGIN ||
    path === ROUTES.AUTH_CALLBACK ||
    path === ROUTES.WELCOME ||
    path === ROUTES.FORGOT_PASSWORD ||
    path === ROUTES.RESET_PASSWORD
  ) {
    return;
  }

  // Validate path is internal: must start with / but not // (blocks protocol-relative URLs)
  // This prevents phishing attacks via open redirect vulnerabilities
  if (!path.startsWith('/') || path.startsWith('//')) {
    return;
  }

  localStorage.setItem(RETURN_TO_KEY, path);
};

/**
 * Helper to get and clear the return URL after login
 * Validates path to prevent open redirect attacks via tampered localStorage
 */
export const getAndClearReturnTo = (): string | null => {
  const returnTo = localStorage.getItem(RETURN_TO_KEY);
  if (returnTo) {
    localStorage.removeItem(RETURN_TO_KEY);

    // Validate path is internal: must start with / but not // (blocks protocol-relative URLs)
    // This prevents attacks where localStorage is manually tampered with external URLs
    if (!returnTo.startsWith('/') || returnTo.startsWith('//')) {
      return null;
    }

    return returnTo;
  }
  return null;
};